import { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { BRAND } from "@blackthorn/shared";
import SEO from "../components/SEO.jsx";

/**
 * Docs content registry. Each entry is a static, hand-written doc page.
 * Adding a doc: add an entry here and a component, then it appears in the
 * sidebar, search filter, and prev/next pagination automatically.
 */
export type DocPage = {
  slug: string;
  title: string;
  section: "Getting started" | "Configuration" | "Reference" | "Self-hosting" | "Troubleshooting";
  summary: string;
  /** Per-page SEO metadata (title/description/keywords for this exact page). */
  seo: { title: string; description: string; keywords: string };
  headings?: { id: string; label: string }[];
  content: React.ReactNode;
};

const p = (slug: string, title: string, section: DocPage["section"], summary: string, seo: DocPage["seo"], content: React.ReactNode): DocPage => ({
  slug, title, section, summary, seo, content,
});

export const DOCS: DocPage[] = [
  p("getting-started", "Getting started", "Getting started",
    "Invite Blackthorn, pick your settings, and get your first member verified in minutes.",
    { title: "Getting started with Blackthorn", description: "Invite the bot, pick your settings, and get your first member verified in a few minutes.", keywords: "blackthorn setup, discord bot invite, verification bot getting started" },
    <>
      <p>Blackthorn is a hosted bot - there's nothing to install. You can go from invite to verified members in a few minutes.</p>
      <h3>1. Invite the bot</h3>
      <p>Use the <strong>Add to Discord</strong> button on the homepage. Blackthorn requests only the permissions it needs: Manage Roles, Manage Messages, Kick Members, View Audit Log, and Send Messages. You need the <em>Manage Server</em> permission in the target server to invite it.</p>
      <h3>2. Open the dashboard</h3>
      <p>Sign in with Discord from the homepage and pick your server. Everything is configured from the dashboard - verification roles, security thresholds, whitelist policy - no config files, no hosting, no downtime.</p>
      <h3>3. Verify yourself</h3>
      <p>Run <code>/verify</code> in your server and follow the link. Sign in with Discord, pass the security check, complete the CAPTCHA, and you're verified. If it works, your server is live.</p>
      <h3>4. Turn on protection</h3>
      <p>Security systems start in dry-run mode: they detect and log, but don't act. Watch the audit log for a day, tune your thresholds, then switch them on for real when you're confident.</p>
      <div className="doc-callout">Commands can take a few minutes to appear after inviting - Discord caches command lists per server.</div>
    </>
  ),
  p("verification", "Verification", "Getting started",
    "How web verification works: Discord sign-in, alt/ban screening, CAPTCHA, tokens, and role sync.",
    { title: "Verification", description: "How the web verification works: Discord sign-in, a security check for alts and banned accounts, the CAPTCHA, and what roles get applied after.", keywords: "web verification, captcha, alt detection, discord role sync" },
    <>
      <p>Blackthorn verifies identity - not just click-through. Verification is done through the web:</p>
      <h3>The flow</h3>
      <p>The member opens the single-use verification link and signs in with Discord, which proves the person opening the link owns the Discord account. Blackthorn then screens the attempt: alt/ban-evasion risk is scored from Discord account age, verification history, and logged verification IPs (hashed), and operator-banned accounts are rejected outright. Only after passing screening does the CAPTCHA page appear; completing it marks the identity verified.</p>
      <h3>Security screening</h3>
      <ul>
        <li><strong>Alt detection</strong> - risk score from new Discord accounts, verification history, and logged verification IPs (salted hashes, never raw); above your configured threshold the attempt is rejected.</li>
        <li><strong>Ban detection</strong> - accounts on the server blocklist are rejected before the CAPTCHA.</li>
        <li><strong>VPN protection</strong> - <em>coming soon</em>. Verification IPs are already logged (as salted hashes) so VPN blocking can be switched on without changing the flow.</li>
      </ul>
      <h3>Tokens</h3>
      <ul>
        <li>Single-use - a link works exactly once.</li>
        <li>Expiring - links stop working after a short window, shown right in the bot's reply.</li>
        <li>Never share the link - it proves account control.</li>
      </ul>
      <h3>Role sync</h3>
      <p>After a successful verification, Blackthorn applies roles: the verified role is granted and unverified roles are removed where configured.</p>
    </>
  ),
  p("security", "Security & anti-raid", "Configuration",
    "Anti-raid thresholds, quarantine, lockdown, spam detection, and audit-log monitoring.",
    { title: "Security & anti-raid", description: "Set join limits, quarantine roles, lockdown, spam filters, and audit-log alerts. Everything starts in dry-run mode - it logs first, acts when you say so.", keywords: "discord anti-raid, anti-spam, quarantine role, server lockdown" },
    <>
      <p>All security systems start in <strong>dry-run mode</strong>: events are detected and logged, but no action is taken until you flip the switch.</p>
      <h3>Anti-raid</h3>
      <p>Blackthorn watches join rate. When joins exceed your threshold inside your time window, configured actions fire: quarantine role, member kick, or server lockdown.</p>
      <h3>Anti-spam</h3>
      <p>Message-rate and content heuristics flag spam. Confirmed spam can be purged and the author timed out automatically.</p>
      <h3>Audit-log monitoring</h3>
      <p>Destructive actions - webhook creation, channel deletion, mass bans, bot additions - are monitored. Alerts go to your configured security channel with the actor, target, and timestamp.</p>
      <h3>Incidents</h3>
      <p>Every raid or spam event is recorded as an incident, visible in the dashboard with its full timeline, so you can review what happened and tune thresholds.</p>
    </>
  ),
  p("whitelist", "Whitelist", "Configuration",
    "Whitelist policy modes and managing entries.",
    { title: "Whitelist", description: "Decide who gets in and keep it that way - approve or remove whitelist entries from the dashboard.", keywords: "whitelist, access control, verified identity" },
    <>
      <p>The whitelist gates access to gated channels based on verified identity.</p>
      <h3>Managing entries</h3>
      <p>Approve or remove members from the dashboard's Whitelist page. Every change is written to the audit log with the acting staff member.</p>
    </>
  ),
  p("dashboard", "Dashboard", "Configuration",
    "A tour of every dashboard page and what it controls.",
    { title: "Dashboard guide", description: "What each dashboard page does: overview, verification, security, whitelist, incidents, and audit logs.", keywords: "blackthorn dashboard, discord bot dashboard, server settings" },
    <>
      <p>The dashboard is where staff configure and monitor Blackthorn without touching a config file. Sign in with Discord - access is checked server-side per guild.</p>
      <h3>Overview</h3>
      <p>At-a-glance status: verification counts, recent incidents, and configuration state for the selected server.</p>
      <h3>Verification</h3>
      <p>Configure verified roles, alt-score thresholds, and token behavior.</p>
      <h3>Security</h3>
      <p>Anti-raid thresholds and actions, spam settings, audit-log monitoring targets, and dry-run switches.</p>
      <h3>Whitelist</h3>
      <p>Review pending whitelist requests, approve or remove entries.</p>
      <h3>Incidents</h3>
      <p>Browse every recorded raid/spam incident with its timeline.</p>
      <h3>Audit logs</h3>
      <p>The full, filterable record of every action taken through the bot or dashboard.</p>
    </>
  ),
  p("commands", "Command reference", "Reference",
    "Every slash command, its subcommands, and required permissions.",
    { title: "Command reference", description: "Every slash command, what it does, and who can use it.", keywords: "discord slash commands, verification command, moderation commands" },
    <>
      <h3><code>/verify</code></h3>
      <table className="doc-table"><thead><tr><th>Option</th><th>Description</th></tr></thead><tbody>
        <tr><td><em>none</em></td><td>Takes no options - always starts the web verification flow.</td></tr>
      </tbody></table>
      <p>Everyone can use this. Replies ephemerally with a single-use verification link.</p>
      <h3>Moderation commands</h3>
      <p>Warnings, cases, timeouts, purges, and incident review. All moderation commands validate role hierarchy before acting, and every action is audited.</p>
      <div className="doc-callout">Permission errors reply ephemerally with the reason - staff never see a silent failure.</div>
    </>
  ),
  p("privacy", "Privacy & data", "Reference",
    "What Blackthorn stores, who can see it, and how to remove your data.",
    { title: "Privacy & data", description: "What gets stored (and what never does), who can see it, and how to get your data removed.", keywords: "blackthorn privacy, data storage, gdpr, data deletion" },
    <>
      <h3>What we store</h3>
      <p>Blackthorn stores the minimum needed to run verification and security: your Discord ID, verification timestamps and method, screening signals (alt score and a salted one-way hash of your IP for alt correlation - never your raw IP), and audit records of actions taken by or on you.</p>
      <h3>What we never store or show</h3>
      <p>Raw IP addresses are never displayed to server staff anywhere. Security signals are surfaced as facts ("a signal was shared"), never as raw data.</p>
      <h3>Who can see your data</h3>
      <p>Server staff can see verification status and audit events for their own server only. Blackthorn operators can access data only for maintenance, under the same restrictions.</p>
      <h3>Removing your data</h3>
      <p>Staff can unlink your identity from the dashboard. For full deletion requests, contact the staff of the server you verified in - they can escalate to the Blackthorn team.</p>
      <div className="doc-callout">Verification links are single-use and expire quickly - an old link in your DMs proves nothing and grants no access.</div>
    </>
  ),
  p("faq", "FAQ", "Troubleshooting",
    "Answers to the questions server owners ask most.",
    { title: "FAQ", description: "Is it free? Does it store IPs? Can members re-verify? Straight answers to the common ones.", keywords: "blackthorn faq, verification bot questions, privacy" },
    <>
      <h3>Is Blackthorn free?</h3>
      <p>Yes - verification, whitelist, security, and moderation are free for every server. There's no premium tier gating core safety features.</p>
      <h3>Do I need to host anything?</h3>
      <p>No. Blackthorn is fully hosted. Invite it, configure it in the dashboard, done.</p>
      <h3>Is verification required for members to chat?</h3>
      <p>That's your choice. Many servers gate a role channel structure behind the verified role.</p>
      <h3>Does Blackthorn store IP addresses?</h3>
      <p>No. See <Link to="/docs/privacy">Privacy &amp; data</Link> - security signals are surfaced as facts, never raw data shown to staff.</p>
      <h3>Can members re-verify on a new account?</h3>
      <p>Yes - switching accounts means running verification again, and everything is logged, so staff can see exactly what happened.</p>
      <h3>Where do I report a bug?</h3>
      <p>Contact the Blackthorn team through the support server linked from the homepage, or ask your server's administrators if you're a member.</p>
    </>
  ),
  p("troubleshooting", "Troubleshooting", "Troubleshooting",
    "Common issues and how to fix them fast.",
    { title: "Troubleshooting", description: "Commands not showing, expired links, role errors, an empty dashboard - here's the fix for each.", keywords: "bot troubleshooting, command not found, role hierarchy error" },
    <>
      <h3><code>/verify</code> replies "Unknown command" (or doesn't appear)</h3>
      <p>Commands can take a few minutes to register after the bot joins - Discord caches command lists per server. If it's been longer: kick and re-invite the bot, or check Discord's status page for API incidents.</p>
      <h3>Verification link says expired</h3>
      <p>Tokens are single-use and time-limited. Run <code>/verify</code> again for a fresh link.</p>
      <h3>Bot can't assign roles</h3>
      <p>Check that the bot's role is <em>above</em> the roles it assigns, and that it has Manage Roles. Role hierarchy beats configuration.</p>
      <h3>Dashboard shows no servers</h3>
      <p>You need <em>Manage Server</em> permission in a server where the bot is installed. Sign out and back in with the right Discord account, and make sure popup blockers aren't eating the OAuth window.</p>
    </>
  ),
];

export const DOC_SECTIONS = ["Getting started", "Configuration", "Reference", "Self-hosting", "Troubleshooting"] as const;

// ═════════════ Full-text search ═════════════
// Built once per page load from the DOCS registry: every doc's title, summary,
// SEO keywords, and rendered body text are flattened into a plain-text index.
// Queries are tokenized; a doc matches only if EVERY term matches it (AND
// semantics), and results are ranked by weighted term frequency plus an
// exact-phrase bonus.

const STOP_WORDS = new Set(["the", "a", "an", "is", "are", "to", "of", "and", "or", "in", "on", "for", "it", "how", "do", "i", "my", "me"]);

/** Recursively extract visible text from the doc's React tree. */
function extractText(node: React.ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(extractText).join(" ");
  if (typeof node === "object" && "props" in (node as unknown as Record<string, unknown>)) {
    const props = (node as { props?: { children?: React.ReactNode } }).props;
    return extractText(props?.children);
  }
  return "";
}

export interface DocSearchResult {
  doc: DocPage;
  /** 0-100 relevance score. */
  score: number;
  /** A snippet of body text with matched terms wrapped in »…« markers. */
  snippet: string;
}

interface DocIndexEntry {
  doc: DocPage;
  titleLower: string;
  metaLower: string; // summary + SEO keywords/description
  bodyLower: string;
  body: string;
}

function normalize(text: string): string {
  return text.toLowerCase().replace(/\s+/g, " ").trim();
}

export function tokenizeQuery(query: string): string[] {
  return query
    .toLowerCase()
    .split(/[^a-z0-9/-]+/)
    .filter((t) => t.length > 1 && !STOP_WORDS.has(t));
}

function countOccurrences(haystack: string, needle: string): number {
  let count = 0;
  let idx = haystack.indexOf(needle);
  while (idx !== -1) {
    count++;
    idx = haystack.indexOf(needle, idx + needle.length);
  }
  return count;
}

/** Build the searchable index from the doc registry. */
export function buildDocIndex(docs: DocPage[]): DocIndexEntry[] {
  return docs.map((doc) => {
    const body = normalize(extractText(doc.content));
    return {
      doc,
      titleLower: normalize(doc.title),
      metaLower: normalize(`${doc.summary} ${doc.seo.keywords} ${doc.seo.description}`),
      bodyLower: body,
      body,
    };
  });
}

function countAll(entry: DocIndexEntry, term: string): { title: number; meta: number; body: number } {
  return {
    title: countOccurrences(entry.titleLower, term),
    meta: countOccurrences(entry.metaLower, term),
    body: countOccurrences(entry.bodyLower, term),
  };
}

function makeSnippet(entry: DocIndexEntry, terms: string[]): string {
  if (terms.length === 0) return entry.body.slice(0, 160) + (entry.body.length > 160 ? "…" : "");
  // Find the earliest occurrence of any term.
  let first = -1;
  for (const t of terms) {
    const i = entry.bodyLower.indexOf(t);
    if (i !== -1 && (first === -1 || i < first)) first = i;
  }
  if (first === -1) return entry.body.slice(0, 160);
  const start = Math.max(0, first - 60);
  const end = Math.min(entry.body.length, first + 180);
  let snippet = (start > 0 ? "…" : "") + entry.body.slice(start, end) + (end < entry.body.length ? "…" : "");
  const escaped = terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|");
  snippet = snippet.replace(new RegExp(`(${escaped})`, "gi"), "»$1«");
  return snippet;
}

/** Search the index. Every query term must appear in the doc (AND); results ranked. */
export function searchDocs(index: DocIndexEntry[], query: string): DocSearchResult[] {
  const terms = tokenizeQuery(query);
  if (terms.length === 0) return [];
  const phrase = normalize(query);
  const results: DocSearchResult[] = [];
  for (const entry of index) {
    let score = 0;
    let allMatch = true;
    for (const term of terms) {
      const c = countAll(entry, term);
      if (c.title + c.meta + c.body === 0) {
        allMatch = false;
        break;
      }
      score +=
        c.title * 30 +            // title hits are strongest
        c.meta * 8 +              // summary/keywords next
        Math.min(c.body, 10) * 3; // body: capped so long docs don't dominate
    }
    if (!allMatch) continue;
    if (phrase.length > 3 && (entry.bodyLower.includes(phrase) || entry.titleLower.includes(phrase) || entry.metaLower.includes(phrase))) {
      score += 25; // exact full-phrase bonus
    }
    results.push({ doc: entry.doc, score: Math.min(100, score), snippet: makeSnippet(entry, terms) });
  }
  return results.sort((a, b) => b.score - a.score || a.doc.title.localeCompare(b.doc.title));
}

// ═════════════ Page layout ═════════════

function useDocIndex(slug: string | undefined) {
  return useMemo(() => {
    const i = DOCS.findIndex((d) => d.slug === slug);
    return { page: i >= 0 ? DOCS[i] : null, prev: i > 0 ? DOCS[i - 1] : null, next: i >= 0 && i < DOCS.length - 1 ? DOCS[i + 1] : null };
  }, [slug]);
}

/** Render a search snippet, turning »term« markers into highlighted marks. */
function Snippet({ text }: { text: string }) {
  const parts = text.split(/(»[^«]*«)/g);
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith("»") && part.endsWith("«") ? (
          <mark key={i} className="bg-bt-accent/25 text-bt-accentHover rounded px-0.5">{part.slice(1, -1)}</mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

export default function DocsLayout(): React.ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const slug = location.pathname.split("/").pop() ?? "";
  const { page, prev, next } = useDocIndex(slug === "" || slug === "docs" ? DOCS[0]!.slug : slug);
  const [query, setQuery] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [selected, setSelected] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const index = useMemo(() => buildDocIndex(DOCS), []);
  const results = useMemo(() => (query.trim() ? searchDocs(index, query) : []), [query, index]);

  // Scroll to top on page change
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  // Reset selection when results change
  useEffect(() => { setSelected(0); }, [query]);

  // Keyboard shortcuts: "/" focuses search, Escape clears it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const typing = target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable;
      if (e.key === "/" && !typing) {
        e.preventDefault();
        searchInputRef.current?.focus();
      } else if (e.key === "Escape" && typing) {
        setQuery("");
        (target as HTMLInputElement).blur();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const goTo = (path: string) => {
    setQuery("");
    searchInputRef.current?.blur();
    navigate(path);
  };

  const showResults = query.trim().length > 0;

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title={page ? `${page.seo.title} - Docs` : "Documentation"}
        description={page ? page.seo.description : `Everything you need to deploy, configure, and run ${BRAND.name}: setup, verification, security, whitelist, and the dashboard.`}
        path={page ? `/docs/${page.slug}` : "/docs"}
        keywords={page?.seo.keywords}
      />
      {/* Docs header - its own identity, distinct from marketing site */}
      <header className="sticky top-0 z-40 border-b border-bt-border bg-bt-bg/95 backdrop-blur">
        <div className="h-14 px-4 flex items-center justify-between gap-3 max-w-7xl mx-auto">
          <Link to="/docs" className="flex items-center gap-2.5 shrink-0">
            <img src="/blackthorn-icon.svg" alt="" className="w-7 h-7" />
            <span className="font-semibold">Docs</span>
            <span className="text-bt-muted text-sm hidden sm:inline">- {BRAND.name}</span>
          </Link>
          <div className="flex items-center gap-3 text-sm min-w-0 relative">
            <input
              ref={searchInputRef}
              type="search"
              placeholder="Search docs…  (press /)"
              aria-label="Search documentation"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") { e.preventDefault(); setSelected((s) => Math.min(s + 1, results.length - 1)); }
                else if (e.key === "ArrowUp") { e.preventDefault(); setSelected((s) => Math.max(s - 1, 0)); }
                else if (e.key === "Enter" && results[selected]) { e.preventDefault(); goTo(`/docs/${results[selected].doc.slug}`); }
              }}
              className="bg-bt-surface border border-bt-border rounded px-3 py-1.5 text-sm w-full min-w-0 max-w-60 focus:outline-none focus:border-bt-steel"
            />
            {/* Search dropdown: full-text results with snippets + keyboard nav */}
            {showResults && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-bt-bg border border-bt-border rounded-lg shadow-2xl shadow-black/50 max-h-[65vh] overflow-y-auto z-50">
                {results.length === 0 ? (
                  <p className="px-4 py-4 text-sm text-bt-muted">
                    No results for “{query}”. Try a feature name like <em>quarantine</em>, <em>captcha</em>, or <em>role sync</em>.
                  </p>
                ) : (
                  <ul className="py-2">
                    {results.map((r, i) => (
                      <li key={r.doc.slug}>
                        <button
                          className={`w-full text-left px-4 py-3 ${i === selected ? "bg-bt-surfaceAlt" : "hover:bg-bt-surfaceAlt/60"}`}
                          onMouseEnter={() => setSelected(i)}
                          onClick={() => goTo(`/docs/${r.doc.slug}`)}
                        >
                          <p className="flex items-center gap-2 text-sm font-medium">
                            <span className="text-bt-accentHover">{r.doc.title}</span>
                            <span className="text-[10px] uppercase tracking-wide text-bt-muted border border-bt-border rounded px-1.5 py-0.5">{r.doc.section}</span>
                            <span className="text-[10px] text-bt-muted ml-auto">{r.score}%</span>
                          </p>
                          <p className="text-xs text-bt-muted mt-1 line-clamp-2"><Snippet text={r.snippet} /></p>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            <Link to="/" className="bg-bt-surface border border-bt-border rounded px-3 py-1.5 text-bt-muted hover:text-bt-text hover:border-bt-steel shrink-0">Back to site</Link>
            {/* Mobile: collapsible page menu */}
            <button
              aria-label={menuOpen ? "Close docs menu" : "Open docs menu"}
              aria-expanded={menuOpen}
              className="md:hidden border border-bt-border rounded p-2 hover:border-bt-steel shrink-0"
              onClick={() => setMenuOpen((v) => !v)}
            >
              {menuOpen ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 6h16M4 12h16M4 18h16"/></svg>
              )}
            </button>
        </div>
        </div>
        {/* Mobile menu - all pages, grouped */}
        {menuOpen && (
          <nav className="md:hidden border-t border-bt-border bg-bt-bg px-4 py-3 max-h-[70vh] overflow-y-auto">
            {DOC_SECTIONS.map((section) => (
              <div key={section} className="mb-3 last:mb-0">
                <p className="text-xs uppercase tracking-widest text-bt-muted mb-1.5">{section}</p>
                <ul className="space-y-1">
                  {DOCS.filter((d) => d.section === section).map((d) => (
                    <li key={d.slug}>
                      <Link
                        to={`/docs/${d.slug}`}
                        className={`block py-1 ${page?.slug === d.slug ? "text-bt-accentHover font-medium" : "text-bt-muted hover:text-bt-text"}`}
                        onClick={() => setMenuOpen(false)}
                      >
                        {d.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        )}
      </header>

      <div className="flex-1 w-full max-w-7xl mx-auto px-4 flex gap-10 py-8">
        {/* Sidebar */}
        <aside className="hidden md:block w-60 shrink-0">
          <nav className="sticky top-24 space-y-6 text-sm max-h-[calc(100vh-8rem)] overflow-y-auto pr-2">
            {DOC_SECTIONS.map((section) => {
              const pages = DOCS.filter((d) => d.section === section);
              if (pages.length === 0) return null;
              return (
                <div key={section}>
                  <p className="text-xs uppercase tracking-widest text-bt-muted mb-2">{section}</p>
                  <ul className="space-y-1 border-l border-bt-border">
                    {pages.map((d) => (
                      <li key={d.slug}>
                        <NavLink
                          to={`/docs/${d.slug}`}
                          className={({ isActive }) =>
                            `block pl-3 py-1 -ml-px border-l border-transparent ${isActive ? "text-bt-text border-l-2 !border-l-bt-accent" : "text-bt-muted hover:text-bt-text"}`
                          }
                        >
                          {d.title}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </nav>
        </aside>

        {/* Content */}
        <main className="flex-1 min-w-0">
          {showResults ? (
            <div className="max-w-3xl">
              <h1 className="text-2xl font-semibold mb-1">
                {results.length > 0 ? `${results.length} result${results.length === 1 ? "" : "s"} for “${query}”` : `No results for “${query}”`}
              </h1>
              <p className="text-sm text-bt-muted mb-6">Every term must match a page - try fewer or broader terms.</p>
              {results.length === 0 ? (
                <ul className="space-y-3">
                  {DOCS.map((d) => (
                    <li key={d.slug}>
                      <Link to={`/docs/${d.slug}`} className="block bg-bt-surface border border-bt-border rounded-lg p-4 hover:border-bt-steel">
                        <p className="font-medium">{d.title}</p>
                        <p className="text-sm text-bt-muted mt-1">{d.summary}</p>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <ul className="space-y-3">
                  {results.map((r) => (
                    <li key={r.doc.slug}>
                      <Link to={`/docs/${r.doc.slug}`} className="block bg-bt-surface border border-bt-border rounded-lg p-4 hover:border-bt-steel">
                        <p className="flex items-center gap-2 font-medium">
                          {r.doc.title}
                          <span className="text-[10px] uppercase tracking-wide text-bt-muted border border-bt-border rounded px-1.5 py-0.5">{r.doc.section}</span>
                          <span className="text-[10px] text-bt-muted ml-auto">{r.score}% match</span>
                        </p>
                        <p className="text-sm text-bt-muted mt-1">{r.doc.summary}</p>
                        <p className="text-sm text-bt-text/80 mt-2"><Snippet text={r.snippet} /></p>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <>
              <article className="max-w-3xl">
                {page ? (
                  <>
                    <p className="text-xs uppercase tracking-widest text-bt-muted mb-2">{page.section}</p>
                    <h1 className="text-3xl font-semibold tracking-tight mb-3">{page.title}</h1>
                    <p className="text-bt-muted mb-8">{page.summary}</p>
                    {page.content}
                  </>
                ) : (
                  <>
                    <h1 className="text-3xl font-semibold tracking-tight mb-3">Blackthorn Documentation</h1>
                    <p className="text-bt-muted mb-8">Everything you need to deploy, configure, and run {BRAND.name}. Press <kbd className="border border-bt-border rounded px-1.5 py-0.5 text-xs bg-bt-surface">/</kbd> anywhere to search.</p>
                    <DocHomeGrid />
                  </>
                )}
              </article>
              {page && (
                <nav className="max-w-3xl mt-16 pt-6 border-t border-bt-border flex justify-between gap-4 text-sm">
                  {prev ? (
                    <Link to={`/docs/${prev.slug}`} className="group">
                      <span className="block text-bt-muted text-xs">Previous</span>
                      <span className="group-hover:text-bt-accentHover">{prev.title}</span>
                    </Link>
                  ) : <span />}
                  {next ? (
                    <Link to={`/docs/${next.slug}`} className="group text-right">
                      <span className="block text-bt-muted text-xs">Next</span>
                      <span className="group-hover:text-bt-accentHover">{next.title}</span>
                    </Link>
                  ) : <span />}
                </nav>
              )}
            </>
          )}
        </main>
      </div>

      <footer className="border-t border-bt-border py-6 text-center text-xs text-bt-muted">
        {BRAND.name} Docs - {BRAND.tagline}
      </footer>
    </div>
  );
}

function DocHomeGrid() {
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      {DOCS.map((d, i) => (
        <Link key={d.slug} to={`/docs/${d.slug}`} className="block bg-bt-surface border border-bt-border rounded-lg p-5 hover:border-bt-steel">
          <p className="text-xs uppercase tracking-widest text-bt-muted mb-1">{d.section} Â· {String(i + 1).padStart(2, "0")}</p>
          <p className="font-medium mb-1">{d.title}</p>
          <p className="text-sm text-bt-muted">{d.summary}</p>
        </Link>
      ))}
    </div>
  );
}
